import { Row } from 'react-table';

interface GapAudioItem extends Object {
    gap_audio_id: string;
    matched_work_id?: Maybe<string>;
    match_options: TrqkGapChannelAudioMatchOption[];
}

/**
 * Group rows based on whether they have been matched to a statement work.
 * - if a row has been matched to a statement work, it is grouped by the matched_work_id
 * - if a row has not been matched to a statement work, it is grouped by the gap_audio_id
 * @param rows - table rows
 * @returns an object where each key is a grouped row ID and each value is an array of grouped rows
 */
// eslint-disable-next-line @typescript-eslint/ban-types -- react-table requires us to extend from object
type GroupByMatchState<Item extends object> = (
    rows: Row<Item>[],
) => Record<string, Row<Item>[]>;

/**
 * Gets a {@link GroupByMatchState} function that utilizes the supplied groupingThreshold parameter.
 * @param summarizeRowsAbove - number of matches for this matched_work_id above which the rows
 * should be summarized into a nested row in the UI.
 * @returns an object where each key is a grouped row ID and each value is an array of grouped rows
 */
export function getGroupByMatchState<Item extends GapAudioItem>(
    summarizeRowsAbove: number,
): GroupByMatchState<Item> {
    const groupByMatchState: GroupByMatchState<Item> = rows => {
        const matchCounts = rows.reduce(
            (acc: Record<string, number>, row: Row<Item>) => {
                const {
                    original: { matched_work_id },
                } = row;

                if (matched_work_id) {
                    if (acc[matched_work_id]) {
                        acc[matched_work_id] += 1;
                    } else {
                        acc[matched_work_id] = 1;
                    }
                }

                return acc;
            },
            {},
        );

        return rows.reduce(
            (acc: Record<string, Row<Item>[]>, row: Row<Item>) => {
                const {
                    original: { gap_audio_id },
                } = row;

                const matchedWorkId = row.original?.matched_work_id ?? null;
                const matchCount = matchedWorkId
                    ? matchCounts[matchedWorkId] ?? 0
                    : 0;

                if (matchedWorkId && matchCount > summarizeRowsAbove) {
                    if (acc[matchedWorkId]) {
                        acc[matchedWorkId].push(row);
                    } else {
                        acc[matchedWorkId] = [row];
                    }
                } else if (acc[gap_audio_id]) {
                    acc[gap_audio_id].push(row);
                } else {
                    acc[gap_audio_id] = [row];
                }

                return acc;
            },
            {},
        );
    };

    return groupByMatchState;
}
